import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { GlobalState } from './global-state';
import { ParentMessagesSender } from './parent-messages-sender';
import { filter, switchMap, take, map, finalize } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { getEvidenceFileMimeType } from '../utils/utils';

@Injectable({ providedIn: 'root' })
export class LlamanosApiService {
  private http = inject(HttpClient);
  private globalState = inject(GlobalState);
  private parentMessagesSender = inject(ParentMessagesSender);
  private apiLlamanos = environment.API_LLAMANOS;
  private readonly categoriasUrl = this.apiLlamanos + '/v1/categorias';
  private readonly syncCategoriasUrl =
    this.apiLlamanos + '/v1/sync/categories-schemas';
  private readonly casoUrl = this.apiLlamanos + '/v1/caso';
  private readonly reinsistirUrl = this.apiLlamanos + '/v1/reinsistir';
  private readonly presignedUrl = this.apiLlamanos + '/v1/presigned-url';

  private buildHeaders(token: string): HttpHeaders {
    return new HttpHeaders({ token: token || '' });
  }

  private normalizeClienteTelefono(body: any): any {
    if (!body || typeof body !== 'object') return body;
    const cliente = (body as any).cliente;
    if (!cliente || typeof cliente !== 'object') return body;

    const telefonoRaw = (cliente as any).telefono;
    if (telefonoRaw === null || telefonoRaw === undefined) return body;

    const telefonoStr = String(telefonoRaw).trim();
    if (telefonoStr === '') return body;

    const digits = telefonoStr.replace(/\D/g, '');
    if (!digits) return body;

    let local = digits;
    if (local.startsWith('569')) {
      local = local.substring(3);
    } else if (local.startsWith('9') && local.length === 9) {
      local = local.substring(1);
    }

    if (local.length > 8) {
      local = local.substring(local.length - 8);
    }

    const normalized = `+569${local}`;
    return {
      ...body,
      cliente: {
        ...cliente,
        telefono: normalized,
      },
    };
  }

  getCategorias(categoria1?: string): Observable<any> {
    this.parentMessagesSender.displayLoader();
    return this.globalState.token$.pipe(
      filter((t) => !!t && t.length > 0),
      take(1),
      switchMap((token) => {
        const headers = this.buildHeaders(token);
        let params: HttpParams | undefined;
        if (categoria1) {
          params = new HttpParams().set('categoria_1', categoria1);
        }
        return this.http.get(this.categoriasUrl, {
          headers,
          params,
        });
      }),
      map((data) => this.cleanCategoriasData(data)),
      finalize(() => {
        this.parentMessagesSender.hideLoader();
        console.log('Se escondio el loader en updateCategorias');
      }),
    );
  }

  private cleanCategoriasData(data: any): any {
    if (!data || typeof data !== 'object') return data;

    const cleanedData = JSON.parse(JSON.stringify(data)); // Deep clone

    // Iterar sobre cada categoría
    Object.keys(cleanedData).forEach((categoriaKey) => {
      const categoria = cleanedData[categoriaKey];

      if (categoria && Array.isArray(categoria)) {
        // La categoría es un array de objetos, donde cada objeto tiene una subcategoría
        categoria.forEach((subcategoriaObj: any, index: number) => {
          if (subcategoriaObj && typeof subcategoriaObj === 'object') {
            // Cada objeto tiene una sola propiedad (el nombre de la subcategoría)
            Object.keys(subcategoriaObj).forEach((subcategoriaKey) => {
              const subcategoriaData = subcategoriaObj[subcategoriaKey];

              if (subcategoriaData && typeof subcategoriaData === 'object') {
                this.cleanSubcategoriaFields(
                  subcategoriaData,
                  `${categoriaKey}[${index}].${subcategoriaKey}`,
                );
              }
            });
          }
        });
      }
    });

    return cleanedData;
  }

  private cleanSubcategoriaFields(subcategoria: any, path: string): void {
    // Limpiar compulsory_fields
    if (Array.isArray(subcategoria.compulsory_fields)) {
      const originalLength = subcategoria.compulsory_fields.length;
      subcategoria.compulsory_fields = subcategoria.compulsory_fields.filter(
        (field: any) => {
          const isValid =
            field !== null &&
            field !== undefined &&
            typeof field === 'string' &&
            field.trim() !== '';
          return isValid;
        },
      );
    }

    // Limpiar optional_fields
    if (Array.isArray(subcategoria.optional_fields)) {
      const originalLength = subcategoria.optional_fields.length;
      subcategoria.optional_fields = subcategoria.optional_fields.filter(
        (field: any) => {
          const isValid =
            field !== null &&
            field !== undefined &&
            typeof field === 'string' &&
            field.trim() !== '';
          return isValid;
        },
      );
    }

    // Limpiar fields
    if (Array.isArray(subcategoria.fields)) {
      const originalLength = subcategoria.fields.length;
      subcategoria.fields = subcategoria.fields.filter((field: any) => {
        let isValid = false;
        if (typeof field === 'string') {
          isValid = field.trim() !== '';
        } else if (typeof field === 'object' && field !== null) {
          // Si es un objeto, verificar que tenga propiedades válidas
          isValid = Object.keys(field).length > 0;
        }
        return isValid;
      });

      // Ordenar por sort ascendente (si no viene, asumir 0)
      subcategoria.fields.sort((a: any, b: any) => {
        const sa = typeof a?.sort === 'number' ? a.sort : 0;
        const sb = typeof b?.sort === 'number' ? b.sort : 0;
        return sa - sb;
      });
    }
  }

  updateCategorias(
    changes: Array<{
      categoria: string;
      subcategoria: string;
      active: boolean;
    }>,
  ): Observable<any> {
    this.parentMessagesSender.displayLoader();
    return this.globalState.token$.pipe(
      filter((t) => !!t && t.length > 0),
      take(1),
      switchMap((token) => {
        const headers = this.buildHeaders(token);
        return this.http.put(this.categoriasUrl, changes, { headers });
      }),
      finalize(() => {
        console.log('Se escondio el loader en updateCategorias');
        this.parentMessagesSender.hideLoader();
      }),
    );
  }

  updateCategoriasSort(
    changes: Array<{
      categoria: string;
      subcategoria: string;
      fields: Array<{ field_name: string; sort: number }>;
    }>,
  ): Observable<any> {
    this.parentMessagesSender.displayLoader();
    return this.globalState.token$.pipe(
      filter((t) => !!t && t.length > 0),
      take(1),
      switchMap((token) => {
        const headers = this.buildHeaders(token);
        return this.http.put(this.categoriasUrl, changes, { headers });
      }),
      finalize(() => {
        console.log('Se escondio el loader en updateCategoriasSort');
        this.parentMessagesSender.hideLoader();
      }),
    );
  }

  getCategoriaSubcategoria(
    categoria1: string,
    categoria2: string,
  ): Observable<any> {
    this.parentMessagesSender.displayLoader();
    return this.globalState.token$.pipe(
      filter((t) => !!t && t.length > 0),
      take(1),
      switchMap((token) => {
        const headers = this.buildHeaders(token);
        const url = `${this.categoriasUrl}/${categoria1}/${categoria2}`;
        return this.http.get(url, { headers });
      }),
      finalize(() => this.parentMessagesSender.hideLoader()),
    );
  }

  syncCategoriesSchemas(): Observable<any> {
    this.parentMessagesSender.displayLoader();
    return this.globalState.token$.pipe(
      filter((t) => !!t && t.length > 0),
      take(1),
      switchMap((token) => {
        const headers = this.buildHeaders(token);
        return this.http.post(this.syncCategoriasUrl, {}, { headers });
      }),
      finalize(() => this.parentMessagesSender.hideLoader()),
    );
  }

  crearCaso(body: any): Observable<any> {
    this.parentMessagesSender.displayLoader();
    const normalizedBody = this.normalizeClienteTelefono(body);
    return this.globalState.token$.pipe(
      filter((t) => !!t && t.length > 0),
      take(1),
      switchMap((token) => {
        const headers = this.buildHeaders(token);
        return this.http.post(this.casoUrl, normalizedBody, { headers });
      }),
      finalize(() => this.parentMessagesSender.hideLoader()),
    );
  }

  getPresignedUrl(nombreArchivo: string, tipoArchivo: string): Observable<any> {
    this.parentMessagesSender.displayLoader();
    return this.globalState.token$.pipe(
      filter((t) => !!t && t.length > 0),
      take(1),
      switchMap((token) => {
        const headers = this.buildHeaders(token);
        const body = { nombreArchivo, tipoArchivo };
        return this.http.post(this.presignedUrl, body, { headers });
      }),
      finalize(() => this.parentMessagesSender.hideLoader()),
    );
  }

  uploadFileToPresignedUrl(urlPrefirmada: string, file: File): Observable<any> {
    this.parentMessagesSender.displayLoader();
    const contentType = getEvidenceFileMimeType(file);
    const headers = contentType
      ? new HttpHeaders({ 'Content-Type': contentType })
      : undefined;
    return this.http
      .put(urlPrefirmada, file, { headers })
      .pipe(finalize(() => this.parentMessagesSender.hideLoader()));
  }

  getCaso(eds?: number): Observable<any> {
    this.parentMessagesSender.displayLoader();
    return this.globalState.token$.pipe(
      filter((t) => !!t && t.length > 0),
      take(1),
      switchMap((token) => {
        const headers = this.buildHeaders(token);
        let params: HttpParams | undefined;
        if (eds !== undefined && eds !== null) {
          params = new HttpParams().set('eds', eds.toString());
        }
        return this.http.get(this.casoUrl, {
          headers,
          params,
        });
      }),
      finalize(() => this.parentMessagesSender.hideLoader()),
    );
  }
}
